import { triggerSuccessConfetti } from "../utils/confetti";
import {
    buildAddNotificationPayload,
    buildBulkAddNotificationPayload,
    buildBulkDeleteNotificationPayload,
    buildDeleteNotificationPayload,
    buildUpdateNotificationPayload
} from "./activityNotificationUtils";
import {
    handleAddItemAchievementStats,
    handleBulkAddAchievementStats
} from "./achievementUtils";

const PERMISSION_ERROR_MESSAGE = "권한 오류 🔒";
const PERMISSION_ERROR_DETAIL = "저장 실패 - 권한이 없거나 오류가 발생했습니다.";
const DELETE_ERROR_DETAIL = "삭제 실패 - 권한이 없거나 오류가 발생했습니다.";
const UPDATE_ERROR_DETAIL = "수정 실패 - 권한이 없거나 오류가 발생했습니다.";

function canSendOrgNotification(userProfile, user) {
    return Boolean(userProfile?.organizationId) && !user?.isAnonymous;
}

function dispatchOrganizationNotification({ userProfile, user, addNotification, payload }) {
    if (!canSendOrgNotification(userProfile, user)) {
        return;
    }

    addNotification(
        payload.title,
        payload.message,
        payload.type,
        payload.category,
        payload.targetId || null,
        userProfile.organizationId,
        payload.targetMenu || null,
        payload.notificationVersion || null
    );
}

export function createDataMutationActions({
    rawAddItem,
    rawUpdateItem,
    rawDeleteItem,
    openAlert,
    addNotification,
    userProfile,
    user,
    incrementStat,
    checkAndAwardBadges,
    students
}) {
    const addItem = async (collectionName, data) => {
        const success = await rawAddItem(collectionName, data);
        if (!success) {
            openAlert(PERMISSION_ERROR_DETAIL, PERMISSION_ERROR_MESSAGE);
            return;
        }

        if (["students", "teachers"].includes(collectionName)) {
            triggerSuccessConfetti();
        }

        const payload = buildAddNotificationPayload({
            collectionName,
            data,
            actorName: userProfile?.name
        });
        dispatchOrganizationNotification({ userProfile, user, addNotification, payload });

        handleAddItemAchievementStats({
            incrementStat,
            collectionName,
            data,
            students
        });
    };

    const bulkAddItem = async (collectionName, items) => {
        const results = await Promise.all(items.map((item) => rawAddItem(collectionName, item)));
        const successCount = results.filter(Boolean).length;

        if (successCount === 0) {
            openAlert(PERMISSION_ERROR_DETAIL, PERMISSION_ERROR_MESSAGE);
            return;
        }

        if (["students", "teachers"].includes(collectionName)) {
            triggerSuccessConfetti();
        }

        const names = items.map((item) => item.name).filter(Boolean);
        const payload = buildBulkAddNotificationPayload({
            collectionName,
            names,
            actorName: userProfile?.name
        });
        dispatchOrganizationNotification({ userProfile, user, addNotification, payload });

        handleBulkAddAchievementStats({
            incrementStat,
            checkAndAwardBadges,
            collectionName,
            successCount
        });
        return true;
    };

    const updateItem = async (collectionName, id, data) => {
        const success = await rawUpdateItem(collectionName, id, data);
        if (!success) {
            openAlert(UPDATE_ERROR_DETAIL, PERMISSION_ERROR_MESSAGE);
            return;
        }

        const payload = buildUpdateNotificationPayload({
            collectionName,
            data,
            actorName: userProfile?.name
        });
        dispatchOrganizationNotification({ userProfile, user, addNotification, payload });
    };

    const deleteItem = async (collectionName, id) => {
        const success = await rawDeleteItem(collectionName, id);
        if (!success) {
            openAlert(DELETE_ERROR_DETAIL, PERMISSION_ERROR_MESSAGE);
            return;
        }

        const payload = buildDeleteNotificationPayload({
            collectionName,
            actorName: userProfile?.name
        });
        dispatchOrganizationNotification({ userProfile, user, addNotification, payload });
    };

    const bulkDeleteItem = async (collectionName, ids, names) => {
        const results = await Promise.all(ids.map((id) => rawDeleteItem(collectionName, id)));
        const successCount = results.filter(Boolean).length;

        if (successCount === 0) {
            openAlert(DELETE_ERROR_DETAIL, PERMISSION_ERROR_MESSAGE);
            return;
        }

        const payload = buildBulkDeleteNotificationPayload({
            collectionName,
            names,
            actorName: userProfile?.name
        });
        dispatchOrganizationNotification({ userProfile, user, addNotification, payload });
        return true;
    };

    return { addItem, bulkAddItem, updateItem, deleteItem, bulkDeleteItem };
}
